import { DateTime } from '@ksyos/date-time';
import { Alert, Button, Card, FormButtonRow, FormGroup, IconButton } from '@ksyos/design-system';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { EmploymentGetOneApiResult } from '@common/interfaces/api/employmentsApi';
import type {
    IOption,
    IOrganization,
    IPractitioner,
    IRegistrationCodes,
    ISpecialization,
} from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { consultationAndReferral, episodeCategoriesWithSpecializations } from '@common/constants';
import { AuthorizationErrorCode, ManagerAuthorization, type Profession, ValidationErrorCode } from '@common/enums';
import { padStartBigCode } from '@common/utils/padStartBigCode';
import { employmentGeneralSettingsSchema } from '@common/validation/employment';
import { employmentPractitionerRegistrationCodesFrontendSchema } from '@common/validation/employmentPractitionerRegistrationCodes';
import { getEmployment } from '@frontend/services/api/employmentsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    getSpecialismCodeByProfession,
    hasOneAuthorization,
    isIdentifierDeclarationOrSupervisorRequired,
    isIdentifierDeclarationRequired,
    isIdentifierRegistrationBigAllowed,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IPersonalSettingsData } from '../types';
import { EmploymentEndedBy } from '../../../elements/EmploymentEndedBy';
import { EndEmploymentsModal } from '../../../elements/modals/EndEmploymentsModal';
import { CancelButton } from '../../../userDataManagement/CancelButton';
import { FormattedDate } from '../../../userDataManagement/FormattedDate';
import { SaveButton } from '../../../userDataManagement/SaveButton';
import { PersonalSettingsAddRegistrationCodesModal } from '../PersonalSettingsAddRegistrationCodesModal';
import { GeneralSettingsProfession } from './GeneralSettingsProfession';
import { GeneralSettingsSpecialism } from './GeneralSettingsSpecialism';
import { GeneralSettingsSpecialization } from './GeneralSettingsSpecialization';
import { GeneralSettingsSupervisor } from './GeneralSettingsSupervisor';
import { convertApiResponseToOptions } from './utils';

interface IPersonalSettingGeneralSettingsProps {
    employmentId: string;
    isCurrentUser: boolean;
    isReadOnly: boolean;
    personalSettingData: IPersonalSettingsData;
}

export interface IEmploymentPractitioner {
    id: string;
    patientsCount: number;
    practitioner: IPractitioner;
    practitionerId: string;
}

export function PersonalSettingsGeneralSettings(props: Readonly<IPersonalSettingGeneralSettingsProps>) {
    const language = useSelector((state: IReduxState) => state.language.code);
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const translate = getSelectedLanguage();

    if (isManager) {
        assertHasAuthorization([
            ManagerAuthorization.employmentSettingsEdit,
            ManagerAuthorization.employmentSettingsRead,
        ]);
    } else if (!props.isCurrentUser) {
        throw new Error(AuthorizationErrorCode.CANNOT_VIEW_OTHER_USERS_EMPLOYMENT_SETTINGS);
    }
    const isSpecializationsVisible = determineIsSpecializationsVisible();

    const abortSignal = useAbortSignal();
    const abortableFetch = useAbortableFetch();

    const [employment, setEmployment] = useState<EmploymentGetOneApiResult>();
    const [initialEmployment, setInitialEmployment] = useState<EmploymentGetOneApiResult>();
    const [employmentPractitioner, setEmploymentPractitioner] = useState<IPractitioner>();
    const [employmentOrganization, setEmploymentOrganization] = useState<IOrganization>();
    const [showEndEmploymentsModal, setShowEndEmploymentsModal] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const [showModal, setShowModal] = useState(false);
    const [showRegistrationCodesAlert, setShowRegistrationCodesAlert] = useState(false);
    const [addRequiredIdentifierDeclarationOrSupervisor, setAddRequiredIdentifierDeclarationOrSupervisor] =
        useState(false);

    const [identifiersErrors, setIdentifiersErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const [updatedSpecializations, setUpdatedSpecializations] = useState<IOption<string>[] | null>(null);
    const [currentSpecializations, setCurrentSpecializations] = useState<IOption<string>[]>([]);
    const [isKsyosManager, setIsKsyosManager] = useState<boolean>();

    useEffect(() => {
        const fetchData = async () => {
            await loadEmployment(props.employmentId);
            await loadSpecializations();
        };

        fetchData();
    }, [props.employmentId]);

    useEffect(() => {
        loadIsKsyosManager(employmentPractitioner?.id);
    }, [employmentPractitioner]);

    if (!employment || !employmentPractitioner || !employmentOrganization) {
        return null;
    }

    return (
        <Card>
            <Card.Transition type={isEditing ? 'edit' : 'read'} />
            <Card.Header
                action={
                    !props.isReadOnly &&
                    (hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit) || isSpecializationsVisible) &&
                    !isEditing ? (
                        <IconButton
                            icon="edit-light"
                            labelText="edit"
                            onClick={() => {
                                setInitialEmployment({ ...employment });
                                setIsEditing(true);
                            }}
                        />
                    ) : undefined
                }
                title={translate.profile.generalSettings}
            />
            <Card.Body>
                {employment.startDate &&
                    renderDateField(translate.common.startingDate, employment.startDate.toString())}
                {employment.endDate && renderDateField(translate.common.endDate, employment.endDate.toString())}

                {employment.endDate && (
                    <EmploymentEndedBy
                        endedByPractitioner={employment.endedByPractitioner}
                        isEndedByOrganization={employment.isEndedByOrganization}
                    />
                )}

                <GeneralSettingsProfession
                    employment={employment}
                    errors={errors}
                    isEditing={hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit) && isEditing}
                    onChange={handleChange}
                    onValidateOne={() => {
                        setErrors(emptyValidationErrors);
                        validateOne('profession', employment.profession);
                    }}
                />
                {isManager && (
                    <GeneralSettingsSupervisor
                        employment={employment}
                        errors={errors}
                        isEditing={isEditing}
                        onChange={handleChange}
                        onValidateOne={validateOne}
                    />
                )}
                <GeneralSettingsSpecialism
                    employment={employment}
                    errors={errors}
                    isEditing={hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit) && isEditing}
                    onChange={handleChange}
                    onValidateOne={validateOne}
                />
                {isSpecializationsVisible && (
                    <GeneralSettingsSpecialization
                        currentSpecializations={currentSpecializations}
                        employmentId={employment.id}
                        isEditing={isEditing}
                        onChange={handleSpecializationChange}
                        showTooltip={true}
                    />
                )}

                {showModal && (
                    <PersonalSettingsAddRegistrationCodesModal
                        errors={identifiersErrors}
                        identifiers={{ ...employment, ...employmentPractitioner }}
                        onCancel={handleRegistrationCodesCancel}
                        onChange={handleRegistrationCodesChange}
                        onSave={async () => await handleRegistrationCodesSubmit()}
                        onValidateOne={handleRegistrationCodesValidateOne}
                        organizationId={employment.organizationId}
                        practitionerId={employment.practitionerId}
                    />
                )}

                <EndEmploymentsModal
                    backUrl={`/employments/${employment.id}/personalSettings`}
                    employmentId={employment.id}
                    handleNo={() => setShowEndEmploymentsModal(false)}
                    isOpen={showEndEmploymentsModal}
                />

                {showRegistrationCodesAlert && renderAlert()}

                {isEditing && (
                    <FormButtonRow>
                        <CancelButton
                            disabled={isSaving}
                            onCancel={async () => handleCancel()}
                        />
                        {canEndEmployment() && (
                            <Button
                                isDisabled={isSaving}
                                onClick={() => setShowEndEmploymentsModal(true)}
                                type="button"
                                variant="outline"
                            >
                                {translate.employment.end}
                            </Button>
                        )}
                        <SaveButton
                            disabled={isSaving}
                            onSave={async () => await handleSubmit(employment)}
                        />
                    </FormButtonRow>
                )}
            </Card.Body>
        </Card>
    );

    function canEndEmployment() {
        return (
            hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit) &&
            (isKsyosManager === false || hasOneAuthorization(ManagerAuthorization.adminsAddEdit)) &&
            employment &&
            (!employment.endDate || DateTime.from(employment.endDate).isAfter(DateTime.now()))
        );
    }

    function renderDateField(label: string, value: string) {
        return (
            <FormGroup label={label}>
                <FormattedDate value={value} />
            </FormGroup>
        );
    }

    function renderAlert() {
        let message = '';

        if (addRequiredIdentifierDeclarationOrSupervisor) {
            message = translate.identifierDeclarationOrRegistration.identifierDeclarationRequiredBecauseOfProfession;
        } else {
            return null;
        }

        return (
            <Alert appearance="warning">
                {message + ' '}
                <Alert.Link
                    onClick={() => setShowModal(true)}
                    variant="button"
                >
                    {translate.common.addHere}
                </Alert.Link>
            </Alert>
        );
    }

    function handleRegistrationCodesChange<K extends keyof IRegistrationCodes>(name: K, value: IRegistrationCodes[K]) {
        if (!employment || !employmentPractitioner) {
            return;
        }

        const cleanedIdentifiersErrors = identifiersErrors
            .remove('identifierDeclaration')
            .remove('identifierRegistrationBig');
        setIdentifiersErrors(cleanedIdentifiersErrors);

        if (name in employment) {
            setEmployment((prevEmployment) => ({ ...prevEmployment, [name]: value }) as EmploymentGetOneApiResult);
        } else if (name in employmentPractitioner) {
            setEmploymentPractitioner((prevPractitioner) => ({ ...prevPractitioner, [name]: value }) as IPractitioner);
        }
    }

    function handleRegistrationCodesValidateOne<K extends keyof IRegistrationCodes>(name: K) {
        setIdentifiersErrors(
            updateValidationErrorsForOne(
                employmentPractitionerRegistrationCodesFrontendSchema,
                { ...employment, ...employmentPractitioner },
                errors,
                name
            )
        );
    }

    function handleRegistrationCodesCancel() {
        setShowModal(false);
        setShowRegistrationCodesAlert(true);
        setIsSaving(false);
        setIdentifiersErrors(emptyValidationErrors);
    }

    function determineIsSpecializationsVisible() {
        return (
            props.personalSettingData.serviceProviders.filter(
                ({ service }) =>
                    service.serviceType &&
                    consultationAndReferral.includes(service.serviceType) &&
                    episodeCategoriesWithSpecializations.includes(service.episodeCategory.name)
            ).length > 0
        );
    }

    async function handleRegistrationCodesSubmit() {
        if (!employment || !employmentPractitioner) {
            return;
        }

        setIsSaving(true);

        const validationResult = validate(employmentPractitionerRegistrationCodesFrontendSchema, {
            ...employment,
            ...employmentPractitioner,
        });

        if (validationResult.hasErrors) {
            setIdentifiersErrors(validationResult.errors);
        } else {
            const paddedIdentifierRegistrationBig = padStartBigCode(employmentPractitioner.identifierRegistrationBig);

            const validatedItem = {
                ...validationResult.item,
                identifierRegistrationBig: paddedIdentifierRegistrationBig,
            };

            const url = `/api/employments/${props.employmentId}/registrationCodes`;
            const result = await abortableFetch.fetchJson(url, 'POST', validatedItem, [
                ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS,
                ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS,
            ]);

            if (result.errorCode) {
                if (result.errorCode === ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS) {
                    setIdentifiersErrors(errors.set('identifierDeclaration', result.errorCode));
                }
                if (result.errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS) {
                    setIdentifiersErrors(errors.set('identifierRegistrationBig', result.errorCode));
                }
            } else {
                setEmploymentPractitioner({
                    ...employmentPractitioner,
                    identifierRegistrationBig: paddedIdentifierRegistrationBig,
                });
                setShowModal(false);
                setShowRegistrationCodesAlert(false);
                setIsEditing(false);
                await loadEmployment(employment.id);
            }
        }
        setIsSaving(false);
    }

    function handleSpecializationChange(specializations: IOption<string>[]) {
        setUpdatedSpecializations(specializations);
    }

    function handleChange<K extends keyof EmploymentGetOneApiResult>(name: K, value: EmploymentGetOneApiResult[K]) {
        if (name === 'profession') {
            if (employment && value !== employment.profession) {
                setEmployment({
                    ...employment,
                    specialismCode: getSpecialismCodeByProfession(value as Profession, employment.specialismCode),
                });
            }
            setShowRegistrationCodesAlert(false);
        }

        setEmployment((prevEmployment) => ({ ...prevEmployment, [name]: value }) as EmploymentGetOneApiResult);
    }

    function validateOne<K extends keyof EmploymentGetOneApiResult>(name: K, value: EmploymentGetOneApiResult[K]) {
        const employmentToValidate = { ...employment, [name]: value } as EmploymentGetOneApiResult;
        setErrors(updateValidationErrorsForOne(employmentGeneralSettingsSchema, employmentToValidate, errors, name));
    }

    async function loadEmployment(employmentId: string) {
        const employmentResult = await getEmployment(employmentId, abortSignal);
        const practitioner = await getPractitioner(employmentResult.practitionerId);
        const organization = await getOrganization(employmentResult.organizationId);

        setEmploymentPractitioner(practitioner);
        setEmploymentOrganization(organization);
        setEmployment(employmentResult);
    }

    async function loadSpecializations() {
        const specializations = await getCurrentSpecializations();
        setCurrentSpecializations(specializations);
    }

    async function loadIsKsyosManager(practitionerId?: string) {
        if (practitionerId && hasOneAuthorization(ManagerAuthorization.employmentSettingsEdit)) {
            const result = await abortableFetch.fetchJson(`/api/employments/${practitionerId}/isKsyosManager`, 'GET');
            setIsKsyosManager(result.json.isKsyosManager);
        }
    }

    async function getCurrentSpecializations() {
        const url = `/api/employmentSpecializations?employmentId=${props.employmentId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const employmentSpecializations = result.json.items as {
            employmentId: string;
            id: string;
            specialization: ISpecialization;
        }[];

        return convertApiResponseToOptions(
            employmentSpecializations.map((employmentSpecialization) => employmentSpecialization.specialization),
            language
        );
    }

    async function getPractitioner(practitionerId: string) {
        const url = `/api/practitioners/${practitionerId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        return result.json as IPractitioner;
    }

    async function getOrganization(organizationId: string) {
        const url = `/api/organizations/${organizationId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');

        return result.json as IOrganization;
    }

    function handleCancel(shouldExitEditing: boolean = true) {
        setErrors(emptyValidationErrors);

        if (shouldExitEditing && initialEmployment) {
            setEmployment(initialEmployment);
            setShowRegistrationCodesAlert(false);
            setIsEditing(false);
        }
    }

    async function handleSubmit(updatedEmployment: EmploymentGetOneApiResult) {
        setIsSaving(true);

        await saveSpecializations(updatedSpecializations);

        if (isManager && !isEqual(updatedEmployment, initialEmployment)) {
            const { errors: validationErrors, item } = validate(employmentGeneralSettingsSchema, updatedEmployment);
            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else if (item) {
                setErrors(emptyValidationErrors);
                if (checkRegistrationCodes({ ...updatedEmployment, ...item })) {
                    setShowModal(true);

                    return;
                } else {
                    await saveEmploymentGeneralSettings(item);
                    await loadEmployment(updatedEmployment.id);
                }
                setIsEditing(false);
            }
        }

        if (isEqual(updatedEmployment, initialEmployment)) {
            setIsEditing(false);
        }

        setShowRegistrationCodesAlert(false);
        setIsSaving(false);
        setInitialEmployment(undefined);
    }

    async function saveSpecializations(specializations: IOption<string>[] | null) {
        if (!specializations) {
            return;
        }

        const url = `/api/employments/${props.employmentId}/specializations`;
        const employmentSpecializations = specializations.map((specialization) => specialization.value);

        await abortableFetch.fetchJson(url, 'POST', employmentSpecializations);

        setUpdatedSpecializations(null);
        await loadSpecializations();
    }

    async function saveEmploymentGeneralSettings(updatedEmployment: Partial<EmploymentGetOneApiResult>) {
        const url = `/api/employments/${props.employmentId}/generalSettings`;
        await abortableFetch.fetchJson(url, 'POST', updatedEmployment);
    }

    function checkRegistrationCodes(updatedEmployment: EmploymentGetOneApiResult) {
        if (updatedEmployment.profession && employmentPractitioner) {
            const identifierDeclarationRequired = isIdentifierDeclarationRequired([updatedEmployment.profession]);
            const identifierDeclarationOrSupervisorRequired = isIdentifierDeclarationOrSupervisorRequired([
                updatedEmployment.profession,
            ]);
            const identifierRegistrationBigAllowed = isIdentifierRegistrationBigAllowed([updatedEmployment.profession]);

            if (
                (!updatedEmployment.identifierDeclarationPractitionerId &&
                    (identifierDeclarationRequired || identifierDeclarationOrSupervisorRequired)) ||
                identifierRegistrationBigAllowed
            ) {
                const addIdentifierDeclaration =
                    (identifierDeclarationRequired || identifierDeclarationOrSupervisorRequired) &&
                    !employmentPractitioner.identifierDeclaration;
                const addIdentifierRegistrationBig =
                    identifierRegistrationBigAllowed && !employmentPractitioner.identifierRegistrationBig;

                setAddRequiredIdentifierDeclarationOrSupervisor(addIdentifierDeclaration);

                return addIdentifierDeclaration || addIdentifierRegistrationBig;
            }
        }

        return false;
    }
}
